import React, { useState, useEffect, useRef } from 'react';
import { X, Users, Clock, Search, Check, ExternalLink, UserX, UserPlus } from 'lucide-react';
import { useSocket } from '../../contexts/SocketContext';
import { useAuth } from '../../contexts/AuthContext';
import axios from '../../config/axios';
import NewUserAvatar from './NewUserAvatar';

const MODE_TO_TYPE = {
    '1v1': 'truco-1v1',
    '2v2': 'truco-2v2',
    '3v3': 'truco-3v3'
};

const MODE_CONFIG = {
    '1v1': { label: '1 vs 1', maxMembers: 1, allowsInvites: false, description: 'Partida individual contra otro jugador' },
    '2v2': { label: '2 vs 2', maxMembers: 2, allowsInvites: true, description: 'Puedes invitar 1 jugador o buscar equipo aleatorio' },
    '3v3': { label: '3 vs 3', maxMembers: 3, allowsInvites: true, description: 'Puedes invitar hasta 2 jugadores o buscar aleatorio' }
};

const PlayerCard = ({ username = 'Sin nombre', image, subtitle, isCurrentUser = false, isReady = false }) => {
    const userForAvatar = { username, image, name: username };

    return (
        <div
            className={`flex items-center justify-between p-4 rounded-xl border-2 transition-all
            ${isCurrentUser ? 'bg-blue-50 border-[#2284DF]' : 'bg-gray-50 border-gray-200'}
            ${isReady ? 'ring-2 ring-green-500' : ''}`}
        >
            <div className="flex items-center space-x-3">
                <NewUserAvatar user={userForAvatar} size="md" />
                <div>
                    <span className="font-semibold text-gray-900">
                        {username} {isCurrentUser && <span className="text-sm text-[#2284DF]">(Tú)</span>}
                    </span>
                    {subtitle && <div className="text-xs text-gray-500">{subtitle}</div>}
                    {isReady && (
                        <div className="flex items-center gap-1 text-green-600 text-sm">
                            <Check className="w-4 h-4" />
                            Listo
                        </div>
                    )}
                </div>
            </div>
            {isReady && (
                <div className="w-6 h-6 bg-green-500 rounded-full flex items-center justify-center">
                    <Check className="w-4 h-4 text-white" />
                </div>
            )}
        </div>
    );
};

const NewQuickPlayModal = ({ onClose }) => {
    const { user } = useAuth();
    const { socket } = useSocket();

    const [gameMode, setGameMode] = useState('1v1');
    const [currentStep, setCurrentStep] = useState('setup');
    const [searchTime, setSearchTime] = useState(0);
    const [estimatedWaitTime, setEstimatedWaitTime] = useState(0);
    const [queueSize, setQueueSize] = useState(0);

    const [matchData, setMatchData] = useState(null);
    const [readyStates, setReadyStates] = useState({ player1Ready: false, player2Ready: false });
    const [readyCheckTime, setReadyCheckTime] = useState(null);
    const [gameUrl, setGameUrl] = useState(null);

    const [partyState, setPartyState] = useState({ party: null, invites: [] });
    const [inviteQuery, setInviteQuery] = useState('');
    const [inviteResults, setInviteResults] = useState([]);
    const [loadingParty, setLoadingParty] = useState(false);
    const [loadingInvite, setLoadingInvite] = useState(false);

    const searchTimerRef = useRef(null);
    const readyTimerRef = useRef(null);

    const gameType = MODE_TO_TYPE[gameMode];
    const modeConfig = MODE_CONFIG[gameMode];
    const isTeamMode = modeConfig.allowsInvites;

    const formatTime = (seconds) => {
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const amIPlayer1 = !!matchData && user.id === matchData.player1Id;
    const amIPlayer2 = !!matchData && user.id === matchData.player2Id;
    const isCaptain = amIPlayer1 || amIPlayer2;

    const myReadyState = isCaptain ? (amIPlayer1 ? readyStates.player1Ready : readyStates.player2Ready) : false;
    const opponentReadyState = isCaptain ? (amIPlayer1 ? readyStates.player2Ready : readyStates.player1Ready) : false;
    const bothReady = readyStates.player1Ready && readyStates.player2Ready;
    const isInProgress = matchData?.status === 'IN_PROGRESS';
    const resolvedGameUrl = gameUrl || (matchData?.matchId ? `${window.location.origin}/games/truco/game/${matchData.matchId}` : null);

    const isLeader = partyState.party?.leaderId === user.id;
    const partyMembers = partyState.party?.members || [];

    const fetchPartyState = async () => {
        if (!isTeamMode) return;
        try {
            setLoadingParty(true);
            const response = await axios.get('/queue/party', { params: { gameType } });
            setPartyState(response.data?.data || { party: null, invites: [] });
        } catch (error) {
            console.error('Error cargando estado del equipo quickplay:', error);
        } finally {
            setLoadingParty(false);
        }
    };

    useEffect(() => {
        if (currentStep === 'setup' && isTeamMode) {
            fetchPartyState();
        }
        if (!isTeamMode) {
            setPartyState({ party: null, invites: [] });
            setInviteQuery('');
            setInviteResults([]);
        }
    }, [gameMode, currentStep]);

    useEffect(() => {
        if (!socket) return;

        const handleQueueJoined = (data) => {
            setCurrentStep('searching');
            setQueueSize(data.queueSize || 0);
            setEstimatedWaitTime(data.estimatedWaitTime || 0);
            startSearchTimer();
        };

        const handleQueueStatusUpdate = (data) => {
            if (data.inQueue && data.status) {
                setQueueSize(data.status.queueSize || 0);
                setEstimatedWaitTime(data.status.estimatedWaitTime || 0);
            }
        };

        const handleMatchFound = (data) => {
            setCurrentStep('match_found');
            setMatchData(data);
            stopSearchTimer();
            if (data.matchId) {
                socket.emit('match_ready_status', { matchId: data.matchId });
            }
        };

        const handleMatchReadyUpdate = (data) => {
            if (matchData?.matchId && data.matchId !== matchData.matchId) return;

            setCurrentStep('ready_check');
            setMatchData((prev) => ({
                ...(prev || {}),
                ...data,
                player1Id: data.player1Id || prev?.player1Id,
                player2Id: data.player2Id || prev?.player2Id,
                player1Name: data.player1Name || prev?.player1Name,
                player2Name: data.player2Name || prev?.player2Name,
                player1Image: data.player1Image || prev?.player1Image,
                player2Image: data.player2Image || prev?.player2Image
            }));

            setReadyStates({
                player1Ready: data.player1Ready || false,
                player2Ready: data.player2Ready || false
            });

            if (data.timeRemaining !== undefined && data.timeRemaining !== null) {
                setReadyCheckTime(data.timeRemaining);
                startReadyTimer(data.timeRemaining);
            }

            if (data.gameUrl) setGameUrl(data.gameUrl);
            if (!data.gameUrl && data.status === 'IN_PROGRESS' && data.matchId) {
                setGameUrl(`${window.location.origin}/games/truco/game/${data.matchId}`);
            }
        };

        const handleQueueLeft = () => {
            resetModalState(false);
        };

        const handleQueueError = (data) => {
            alert(data.message || 'Error en el sistema de cola');
            resetModalState(false);
        };

        const handlePartyUpdate = (payload) => {
            if (isTeamMode) {
                setPartyState(payload || { party: null, invites: [] });
            }
        };

        const handlePartyInvites = (invites) => {
            setPartyState((prev) => ({ ...prev, invites: invites || [] }));
        };

        socket.on('queue_joined', handleQueueJoined);
        socket.on('queue_status_update', handleQueueStatusUpdate);
        socket.on('match_found', handleMatchFound);
        socket.on('match_ready_update', handleMatchReadyUpdate);
        socket.on('queue_left', handleQueueLeft);
        socket.on('queue_error', handleQueueError);
        socket.on('quickplay_party_update', handlePartyUpdate);
        socket.on('quickplay_party_invites', handlePartyInvites);

        return () => {
            socket.off('queue_joined', handleQueueJoined);
            socket.off('queue_status_update', handleQueueStatusUpdate);
            socket.off('match_found', handleMatchFound);
            socket.off('match_ready_update', handleMatchReadyUpdate);
            socket.off('queue_left', handleQueueLeft);
            socket.off('queue_error', handleQueueError);
            socket.off('quickplay_party_update', handlePartyUpdate);
            socket.off('quickplay_party_invites', handlePartyInvites);
        };
    }, [socket, matchData?.matchId, isTeamMode]);

    useEffect(() => {
        return () => {
            stopSearchTimer();
            stopReadyTimer();
        };
    }, []);

    const startSearchTimer = () => {
        stopSearchTimer();
        searchTimerRef.current = setInterval(() => setSearchTime((prev) => prev + 1), 1000);
    };

    const stopSearchTimer = () => {
        if (searchTimerRef.current) {
            clearInterval(searchTimerRef.current);
            searchTimerRef.current = null;
        }
    };

    const startReadyTimer = (initialTime) => {
        stopReadyTimer();
        setReadyCheckTime(initialTime);
        readyTimerRef.current = setInterval(() => {
            setReadyCheckTime((current) => {
                if (current <= 0) {
                    stopReadyTimer();
                    return 0;
                }
                return current - 1;
            });
        }, 1000);
    };

    const stopReadyTimer = () => {
        if (readyTimerRef.current) {
            clearInterval(readyTimerRef.current);
            readyTimerRef.current = null;
        }
    };

    const resetModalState = (refreshParty = true) => {
        setCurrentStep('setup');
        setSearchTime(0);
        setEstimatedWaitTime(0);
        setQueueSize(0);
        setMatchData(null);
        setReadyStates({ player1Ready: false, player2Ready: false });
        setReadyCheckTime(null);
        setGameUrl(null);
        stopSearchTimer();
        stopReadyTimer();

        if (refreshParty && isTeamMode) {
            fetchPartyState();
        }
    };

    const handleSearchUsers = async () => {
        const query = inviteQuery.trim();
        if (query.length < 2) {
            setInviteResults([]);
            return;
        }
        try {
            const response = await axios.get('/queue/users/search', {
                params: { q: query, gameType }
            });
            setInviteResults(response.data?.data || []);
        } catch (error) {
            console.error('Error buscando usuarios:', error);
            setInviteResults([]);
        }
    };

    const handleInviteUser = async (invitedUserId) => {
        try {
            setLoadingInvite(true);
            await axios.post('/queue/invite', { invitedUserId, gameType });
            await fetchPartyState();
            setInviteResults([]);
            setInviteQuery('');
        } catch (error) {
            alert(error.response?.data?.message || 'No se pudo enviar la invitación');
        } finally {
            setLoadingInvite(false);
        }
    };

    const handleRespondInvite = async (inviteId, action) => {
        try {
            await axios.post(`/queue/invite/${inviteId}/respond`, { action });
            await fetchPartyState();
        } catch (error) {
            alert(error.response?.data?.message || 'No se pudo responder la invitación');
        }
    };

    const handleLeaveParty = async () => {
        try {
            await axios.post('/queue/party/leave');
            await fetchPartyState();
        } catch (error) {
            alert(error.response?.data?.message || 'No se pudo salir del equipo');
        }
    };

    const handleSearchMatch = () => {
        if (!socket) {
            alert('Error de conexión. Intenta de nuevo.');
            return;
        }

        socket.emit('queue_join', { gameType });
    };

    const handleCancelSearch = () => {
        if (socket) socket.emit('queue_leave');
        resetModalState();
    };

    const handleReady = async () => {
        if (!matchData?.matchId) return;
        if (!isCaptain) return;

        try {
            await axios.post(`/matches/${matchData.matchId}/ready`);
        } catch (error) {
            alert(error.response?.data?.message || 'Error al confirmar ready. Intenta de nuevo.');
        }
    };

    const handleStartGame = () => {
        if (!resolvedGameUrl) return;
        window.open(resolvedGameUrl, '_blank');
        onClose();
    };

    const handleClose = async () => {
        if (currentStep === 'searching' && socket) {
            socket.emit('queue_leave');
        }

        if (currentStep === 'ready_check' && myReadyState && matchData?.matchId && isCaptain) {
            try {
                await axios.post(`/matches/${matchData.matchId}/cancel-ready`);
            } catch (error) {
                console.error('Error al cancelar ready:', error);
            }
        }

        stopSearchTimer();
        stopReadyTimer();
        onClose();
    };

    const renderModeCard = (modeKey) => {
        const cfg = MODE_CONFIG[modeKey];
        const selected = gameMode === modeKey;
        return (
            <div
                key={modeKey}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all
                    ${selected ? 'border-[#2284DF] bg-blue-50 ring-2 ring-[#2284DF]/30' : 'border-gray-200 hover:border-gray-300'}`}
                onClick={() => setGameMode(modeKey)}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <Users className="w-6 h-6 text-[#2284DF]" />
                        <div>
                            <h4 className="font-bold text-[#122436]">{cfg.label}</h4>
                            <p className="text-sm text-gray-600">{cfg.description}</p>
                        </div>
                    </div>
                    {selected && (
                        <div className="w-5 h-5 bg-[#2284DF] rounded-full flex items-center justify-center">
                            <Check className="w-3 h-3 text-white" />
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderPartySetup = () => {
        if (!isTeamMode) return null;

        return (
            <div className="space-y-4 p-4 rounded-xl border-2 border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                    <h4 className="font-bold text-[#122436] uppercase">Tu equipo</h4>
                    <span className="text-sm font-semibold text-gray-600">
                        {partyMembers.length || 1}/{modeConfig.maxMembers}
                    </span>
                </div>

                {loadingParty ? (
                    <p className="text-sm text-gray-500">Cargando equipo...</p>
                ) : (
                    <>
                        {partyMembers.length > 0 ? (
                            <div className="space-y-2">
                                {partyMembers.map((member) => (
                                    <div key={member.id} className="flex items-center justify-between bg-white p-2 rounded-lg">
                                        <div className="flex items-center gap-2">
                                            <NewUserAvatar user={member} size="sm" />
                                            <span className="text-sm font-semibold text-[#122436]">
                                                {member.username} {member.id === partyState.party?.leaderId ? '(Líder)' : ''}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">Aún no tienes equipo armado.</p>
                        )}

                        {partyState.party && (
                            <button
                                onClick={handleLeaveParty}
                                className="w-full h-10 bg-gray-700 text-white rounded-lg text-sm font-bold hover:bg-gray-800"
                            >
                                Salir del equipo
                            </button>
                        )}

                        <div className="pt-2 border-t border-gray-200 space-y-2">
                            <p className="text-xs font-semibold text-gray-600 uppercase">Invitar jugadores</p>
                            {isLeader || !partyState.party ? (
                                <>
                                    <div className="flex gap-2">
                                        <input
                                            value={inviteQuery}
                                            onChange={(e) => setInviteQuery(e.target.value)}
                                            placeholder="Buscar por username"
                                            className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                        />
                                        <button
                                            onClick={handleSearchUsers}
                                            className="px-3 py-2 bg-[#2284DF] text-white rounded-lg text-sm font-bold"
                                        >
                                            Buscar
                                        </button>
                                    </div>
                                    {inviteResults.length > 0 && (
                                        <div className="space-y-2 max-h-40 overflow-auto">
                                            {inviteResults.map((u) => (
                                                <div key={u.id} className="flex items-center justify-between bg-white p-2 rounded-lg">
                                                    <div className="flex items-center gap-2">
                                                        <NewUserAvatar user={u} size="sm" />
                                                        <span className="text-sm font-semibold text-[#122436]">{u.username}</span>
                                                    </div>
                                                    <button
                                                        onClick={() => handleInviteUser(u.id)}
                                                        disabled={loadingInvite}
                                                        className="px-2 py-1 bg-[#2284DF] text-white rounded text-xs font-bold disabled:opacity-60"
                                                    >
                                                        <UserPlus className="w-3 h-3 inline mr-1" />Invitar
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            ) : (
                                <p className="text-xs text-gray-500">Solo el líder puede enviar invitaciones.</p>
                            )}
                        </div>

                        {partyState.invites?.length > 0 && (
                            <div className="pt-2 border-t border-gray-200 space-y-2">
                                <p className="text-xs font-semibold text-gray-600 uppercase">Invitaciones pendientes</p>
                                {partyState.invites.map((invite) => (
                                    <div key={invite.id} className="bg-white p-2 rounded-lg">
                                        <div className="text-sm font-semibold text-[#122436] mb-2">
                                            Invitación de {invite.fromUsername}
                                        </div>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => handleRespondInvite(invite.id, 'ACCEPT')}
                                                className="flex-1 py-1 bg-green-600 text-white rounded text-xs font-bold"
                                            >
                                                Aceptar
                                            </button>
                                            <button
                                                onClick={() => handleRespondInvite(invite.id, 'REJECT')}
                                                className="flex-1 py-1 bg-red-600 text-white rounded text-xs font-bold"
                                            >
                                                Rechazar
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>
        );
    };

    const renderContent = () => {
        switch (currentStep) {
            case 'setup':
                return (
                    <div className="space-y-6">
                        <div>
                            <h3 className="text-lg font-extrabold text-[#122436] uppercase tracking-wide mb-4">
                                Selecciona el modo de juego
                            </h3>
                            <div className="space-y-3">
                                {Object.keys(MODE_CONFIG).map(renderModeCard)}
                            </div>
                        </div>

                        {renderPartySetup()}

                        <button
                            onClick={handleSearchMatch}
                            className="w-full h-12 md:h-14 bg-[#2284DF] text-white rounded-xl font-extrabold uppercase tracking-wide
                                     hover:bg-[#2A91F4] transition-colors flex items-center justify-center gap-2
                                     border-2 border-[#1E75C6]"
                        >
                            <Search className="w-5 h-5" />
                            Buscar Partida
                        </button>
                    </div>
                );

            case 'searching':
                return (
                    <div className="space-y-6 text-center">
                        <div className="space-y-4">
                            <div className="w-16 h-16 mx-auto">
                                <div className="animate-spin rounded-full h-16 w-16 border-4 border-[#2284DF] border-t-transparent"></div>
                            </div>
                            <h3 className="text-xl font-extrabold text-[#122436] uppercase">Buscando oponente...</h3>
                            <p className="text-gray-600 font-semibold">Modo: {MODE_CONFIG[gameMode].label}</p>
                        </div>

                        <div className="bg-gray-50 rounded-xl p-4 space-y-3 border-2 border-gray-200">
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600 font-semibold">Tiempo en cola:</span>
                                <span className="font-mono text-lg font-extrabold text-[#2284DF]">{formatTime(searchTime)}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600 font-semibold">Jugadores en cola:</span>
                                <span className="font-bold">{queueSize}</span>
                            </div>
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-gray-600 font-semibold">Tiempo estimado:</span>
                                <span className="font-bold">~{estimatedWaitTime}s</span>
                            </div>
                        </div>

                        <button
                            onClick={handleCancelSearch}
                            className="w-full h-12 md:h-14 bg-gray-600 text-white rounded-xl font-extrabold uppercase tracking-wide
                                     hover:bg-gray-700 transition-colors flex items-center justify-center gap-2"
                        >
                            <UserX className="w-5 h-5" />
                            Cancelar Búsqueda
                        </button>
                    </div>
                );

            case 'match_found':
            case 'ready_check':
                return (
                    <div className="space-y-6">
                        <div className="text-center">
                            <h3 className="text-xl font-extrabold text-[#122436] uppercase mb-2">
                                {currentStep === 'match_found' ? '¡Match Encontrado!' : 'Preparación del Match'}
                            </h3>
                            {readyCheckTime !== null && (
                                <div
                                    className={`flex items-center justify-center gap-2 text-lg font-extrabold
                                    ${readyCheckTime < 60 ? 'text-red-500' : 'text-[#2284DF]'}`}
                                >
                                    <Clock className="w-5 h-5" />
                                    {formatTime(readyCheckTime)}
                                </div>
                            )}
                        </div>

                        <div className="space-y-3">
                            <PlayerCard
                                username={matchData?.player1Name || 'Equipo 1'}
                                image={matchData?.player1Image}
                                subtitle={matchData?.teamSize > 1 ? 'Capitán equipo 1' : null}
                                isCurrentUser={amIPlayer1}
                                isReady={readyStates.player1Ready}
                            />
                            <PlayerCard
                                username={matchData?.player2Name || 'Equipo 2'}
                                image={matchData?.player2Image}
                                subtitle={matchData?.teamSize > 1 ? 'Capitán equipo 2' : null}
                                isCurrentUser={amIPlayer2}
                                isReady={readyStates.player2Ready}
                            />
                        </div>

                        {matchData?.teamSize > 1 && !isCaptain && (
                            <div className="p-3 bg-amber-50 border-2 border-amber-200 rounded-xl text-sm text-amber-700 font-semibold">
                                Eres miembro del equipo. El líder/capitán debe confirmar el ready.
                            </div>
                        )}

                        {readyCheckTime !== null && (
                            <div className="p-3 bg-blue-50 border-2 border-[#2284DF] rounded-xl">
                                <p className="text-sm text-[#122436] font-semibold">
                                    {myReadyState && !opponentReadyState
                                        ? 'Esperando al rival.'
                                        : !myReadyState && opponentReadyState
                                            ? 'El rival ya confirmó ready.'
                                            : !myReadyState && !opponentReadyState
                                                ? 'Ambos capitanes deben confirmar ready para iniciar.'
                                                : '¡Excelente! Iniciando partida...'}
                                </p>
                            </div>
                        )}

                        {(bothReady || isInProgress) && resolvedGameUrl ? (
                            <button
                                onClick={handleStartGame}
                                className="w-full h-12 md:h-14 bg-green-600 text-white rounded-xl font-extrabold uppercase tracking-wide
                                         hover:bg-green-700 transition-colors flex items-center justify-center gap-2"
                            >
                                <ExternalLink className="w-5 h-5" />
                                Iniciar Partida
                            </button>
                        ) : (
                            <button
                                onClick={handleReady}
                                disabled={myReadyState || !isCaptain}
                                className="w-full h-12 md:h-14 bg-[#2284DF] text-white rounded-xl font-extrabold uppercase tracking-wide
                                         hover:bg-[#2A91F4] transition-colors disabled:opacity-50
                                         disabled:cursor-not-allowed flex items-center justify-center gap-2
                                         border-2 border-[#1E75C6]"
                            >
                                {isCaptain
                                    ? (myReadyState ? 'Esperando Oponente' : 'Estoy Listo')
                                    : 'Esperando al Capitán'}
                            </button>
                        )}
                    </div>
                );

            default:
                return <div>Estado desconocido</div>;
        }
    };

    return (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl p-6 max-w-2xl w-full relative shadow-2xl max-h-[90vh] overflow-y-auto">
                <button
                    onClick={handleClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                    <X className="w-6 h-6" />
                </button>

                <h2 className="toon text-3xl text-[#122436] mb-6 uppercase">Juego Rápido</h2>
                {renderContent()}
            </div>
        </div>
    );
};

export default NewQuickPlayModal;
